<template>
	<!-- To be filled by specific implementations -->
</template>

<script>
import SelectBaseComponent from "@/components/templates/common/SelectBaseComponent";

export default {
	extends: SelectBaseComponent,
	props: {
		optionsChildrenField: { type: String, default: "children" },
		api: { type: Function, default: null }, // API function to load options
		showOnlyChecked: { type: Boolean, default: false }
	},
	data() {
		return {
			// We need to keep extra options for the tree, because reusing the default options property and updating it with the "mapped" tree data leads to the tree not rendering properly.
			// Note that this is not a problem as long as the SelectBaseComponent does not modify the options (does so only on search, which we have overwritten for the tree anyway).
			treeOptions: []
		};
	},
	created() {
		if (this.optionsKeyField && (this.optionsKeyField !== this.optionsValueField)) {
			console.error("tree select needs option keys and values to be equal");
		}

		if (this.optionsKeyGetter || this.optionsValueGetter) {
			console.error("tree select does not support key or value getters yet");
		}

		// make sure keys field is set equal to values
		this.optionsKeyField = this.optionsValueField;

		// Make sure to use the provided tree filtering instead
		this.searchCustom = true;

		this.prepareOptionsForTree();

		if (this.api) {
			this.loadDataFromApi();
		}
	},
	mounted() {
		this.syncTreeState();
	},
	computed: {
		treeProperties() {
			return {
				children: this.optionsChildrenField,
				// the tree does not support getter functions or nested paths here, so we manually map it based on the options' field configuration
				label: "node_label",
				nodeKey: "node_key",
			}
		},
	},
	methods: {
		isTreeRendered() {
			return !!this.$refs.tree;
		},
		filterTreeNode(_, data, node) {
			if (this.showOnlyChecked && !node.checked) {
				return false;
			}

			return !this.searchValue || data[this.treeProperties.label].toLowerCase().includes(this.searchValue.toLowerCase());
		},
		onNodeChecked() {
			const checkedValues = this.$refs.tree.getCheckedNodes(true).map(this.getOptionValue);

			this.$emit("input", checkedValues);
			this.$emit("md-selected", checkedValues);

			if (this.showOnlyChecked) {
				this.applyTreeFilter();
			}
		},
		expandAll() {
			if (this.showOnlyChecked) {
				this.expandVisible();
			} else {
				this.callOnAllNodes((node) => node.expanded = true);
			}
		},
		collapseAll() {
			if (this.showOnlyChecked) {
				this.collapseVisible();
			} else {
				this.callOnAllNodes((node) => node.expanded = false);
			}
		},
		expandVisible() {
			this.callOnAllNodes((node) => {
				if (node.visible) {
					node.expanded = true;
				}
			});
		},
		collapseVisible() {
			this.callOnAllNodes((node) => {
				if (node.visible) {
					node.expanded = false;
				}
			});
		},
		callOnAllNodes(func) {
			const traverseOptionsAsNodes = (options) => {
				options.forEach(option => {
					let node = this.$refs.tree.getNode(option[this.treeProperties.nodeKey]);

					func(node);

					let children = option[this.treeProperties.children];
					if (children) {
						traverseOptionsAsNodes(children);
					}
				});
			};
			traverseOptionsAsNodes(this.treeOptions);
		},
		applyTreeFilter() {
			if (this.isTreeRendered()) {
				this.$refs.tree.filter();
			}
		},
		syncTreeState() {
			if (this.isTreeRendered()) {
				// Make sure nodes are marked as checked before applying the filter, otherwise the filter will work on an outdated "checked" state
				this.$refs.tree.setCheckedKeys(this.value || []);
				this.applyTreeFilter();
			}
		},
		loadDataFromApi() {
			this.api()
				.then(res => {
					this.options = res;
				})
				.catch(this.toast);
		},
		prepareOptionsForTree() {
			this.treeOptions = (this.options || []).map(this.mapOptionAsTreeNode);
		},
		mapOptionAsTreeNode(option) {
			const treeNode = {
				...option
			};
			treeNode[this.treeProperties.nodeKey] = this.getOptionValue(option);
			treeNode[this.treeProperties.label] = this.getOptionDisplayValue(option);

			let children = option[this.treeProperties.children];
			if (children) {
				treeNode[this.treeProperties.children] = children.map(this.mapOptionAsTreeNode);
			}

			return treeNode;
		}
	},
	watch: {
		searchValue() {
			this.applyTreeFilter();
		},
		value() {
			this.syncTreeState();
		},
		options() {
			this.prepareOptionsForTree();
			this.syncTreeState();
		},
		showOnlyChecked() {
			this.applyTreeFilter();

			if (this.showOnlyChecked) {
				this.expandVisible();
			}
		}
	},
};
</script>
